import { appInsights } from '../../../../appInsights';
import { getAuthTokenOrDefault } from '../../api/auth/getAuthToken';
import { mapTimeslot } from '../../api/timeslots/mapTimeslot';
import { timeslotsApi } from '../../api/timeslots/timeslotsApi';
import { currentCartFlow } from '../cart/currentCartFlow';

const fetchForPostalCode = async ({
    b2bRoute = true,
    postalCode,
}: {
    b2bRoute: boolean;
    postalCode: string;
}) => {
    const currentUser = b2bRoute ? 'anonymousb2b' : 'anonymous';

    const timeslotsList = await timeslotsApi.getTimeslotsForPostalCode({
        availableDate: new Date(),
        postalCode: postalCode!,
        userId: currentUser,
        days: 30,
    });

    const timeslots = timeslotsList.listOfTimeWindowListWsDTO.flatMap((x) => x.timeWindowListWsDTO);
    return timeslots.map(mapTimeslot).filter(Boolean) || [];
};

const fetchForPointOfService = async ({
    b2bRoute,
    pointOfService,
}: {
    b2bRoute: boolean;
    pointOfService: string;
}) => {
    const currentUser = b2bRoute ? 'anonymousb2b' : 'anonymous';
    const timeslotsList = await timeslotsApi.getTimeslotsForPointOfService({
        availableDate: new Date(),
        pointOfService,
        userId: currentUser,
        days: 30,
    });
    return timeslotsList.timeWindowListWsDTO.map(mapTimeslot).filter(Boolean);
};

const fetchForCurrentCart = async () => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const cartId = currentCartFlow.getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'fetchForCurrentCart' });
        throw error;
    }

    const timeslotsList = await timeslotsApi.getTimeslots(
        shoppingUserId,
        cartId,
        new Date(),
        token,
    );
    const timeslots = timeslotsList.listOfTimeWindowListWsDTO.flatMap((x) => x.timeWindowListWsDTO);
    return timeslots.map(mapTimeslot).filter(Boolean);
};

const getCurrentTimeslot = async () => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const cartId = currentCartFlow.getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'getCurrentTimeslot' });
        throw error;
    }

    const timeslot = await timeslotsApi.getCurrentTimeslot(shoppingUserId, cartId, token);
    return mapTimeslot(timeslot);
};

const selectTimeslot = async (timeslotId: string) => {
    const { token, shoppingUserId } = await getAuthTokenOrDefault();
    const cartId = currentCartFlow.getCurrentCartId();
    if (!cartId) {
        const error = Error('No cart id');
        appInsights.trackException({ exception: error }, { place: 'selectTimeslot' });
        throw error;
    }

    await timeslotsApi.selectTimeslot(timeslotId, shoppingUserId, cartId, token);
};

const timeslotFlow = {
    fetchForCurrentCart,
    getCurrentTimeslot,
    selectTimeslot,
    fetchForPostalCode,
    fetchForPointOfService,
};

export default timeslotFlow;
